// These are measured media-stage delays, not end-to-end input latency.
// Use interval deltas so an old startup spike doesn't hide current behavior.
function counterDelta(s, key, previous) {
  if (!s || !Number.isFinite(s[key])) return null;
  const old = previous.get(s.id)?.[key];
  return Number.isFinite(old) && s[key] >= old ? s[key] - old : s[key];
}
function bitrate(s, key, previous) {
  const elapsed = s?.timestamp - previous.get(s?.id)?.timestamp;
  const bytes = counterDelta(s, key, previous);
  return elapsed > 0 && bytes !== null ? Math.round(bytes * 8 / elapsed) : null;
}
export function readGameMediaStats(report, previous = new Map()) {
  const stats = [...report.values()];
  const video = stats.find(s => s.type === 'inbound-rtp' && (s.kind || s.mediaType) === 'video');
  const audio = stats.find(s => s.type === 'inbound-rtp' && (s.kind || s.mediaType) === 'audio');
  const selected = stats.find(s => s.type === 'transport')?.selectedCandidatePairId;
  const pair = stats.find(s => s.type === 'candidate-pair' && (selected ? s.id === selected : s.state === 'succeeded' && s.nominated));
  const candidates = stats.filter(s => s.id === pair?.localCandidateId || s.id === pair?.remoteCandidateId);
  const delta = (s, key) => {
    if (!s || !Number.isFinite(s[key])) return null;
    const old = previous.get(s.id)?.[key];
    return Number.isFinite(old) && s[key] >= old ? s[key] - old : s[key];
  };
  const average = (s, numerator, denominator) => {
    const sum = delta(s, numerator), count = delta(s, denominator);
    return sum !== null && count > 0 ? Math.round(sum * 1000 / count) : null;
  };
  const elapsed = video?.timestamp - (previous.get(video?.id)?.timestamp ?? video?.timestamp);
  let lost = 0, received = 0;
  for (const s of [video, audio]) { lost += Math.max(0, delta(s, 'packetsLost') || 0); received += Math.max(0, delta(s, 'packetsReceived') || 0); }
  const decoded = delta(video, 'framesDecoded');
  const fps = Number.isFinite(video?.framesPerSecond) ? video.framesPerSecond : elapsed > 0 && decoded !== null ? decoded * 1000 / elapsed : null;
  const result = {
    mediaRttMs: Number.isFinite(pair?.currentRoundTripTime) ? Math.round(pair.currentRoundTripTime * 1000) : null,
    route: candidates.some(c => c.candidateType === 'relay') ? 'TURN relay' : candidates.length ? 'Direct' : null,
    videoBufferMs: average(video, 'jitterBufferDelay', 'jitterBufferEmittedCount'),
    audioBufferMs: average(audio, 'jitterBufferDelay', 'jitterBufferEmittedCount'),
    decodeMs: average(video, 'totalDecodeTime', 'framesDecoded'),
    videoFps: Number.isFinite(fps) ? Math.round(fps) : null,
    width: video?.frameWidth || null, height: video?.frameHeight || null,
    lossPercent: received + lost > 0 ? Number((lost * 100 / (received + lost)).toFixed(1)) : null,
    videoKbps: bitrate(video, 'bytesReceived', previous),
    audioKbps: bitrate(audio, 'bytesReceived', previous),
    framesDropped: delta(video, 'framesDropped'),
    freezes: delta(video, 'freezeCount'),
    audioConcealedPercent: delta(audio, 'totalSamplesReceived') > 0 && delta(audio, 'concealedSamples') !== null
      ? Number((100 * delta(audio, 'concealedSamples') / delta(audio, 'totalSamplesReceived')).toFixed(2)) : null,
    audioEnergy: delta(audio, 'totalSamplesDuration') > 0 && delta(audio, 'totalAudioEnergy') !== null
      ? Number((delta(audio, 'totalAudioEnergy') / delta(audio, 'totalSamplesDuration')).toFixed(6)) : null,
    videoCodec: stats.find(s => s.id === video?.codecId)?.mimeType || null,
  };
  for (const s of [video, audio]) if (s) previous.set(s.id, s);
  return result;
}

export function readGameSenderStats(report, previous = new Map()) {
  const stats = [...report.values()];
  const video = stats.find(s => s.type === 'outbound-rtp' && (s.kind || s.mediaType) === 'video');
  const audio = stats.find(s => s.type === 'outbound-rtp' && (s.kind || s.mediaType) === 'audio');
  const source = stats.find(s => s.id === video?.mediaSourceId);
  const selected = stats.find(s => s.type === 'transport')?.selectedCandidatePairId;
  const pair = stats.find(s => s.type === 'candidate-pair' && (selected ? s.id === selected : s.state === 'succeeded' && s.nominated));
  const candidates = stats.filter(s => s.id === pair?.localCandidateId || s.id === pair?.remoteCandidateId);
  const frames = counterDelta(video, 'framesEncoded', previous);
  const encode = counterDelta(video, 'totalEncodeTime', previous);
  const elapsed = video?.timestamp - previous.get(video?.id)?.timestamp;
  const result = {
    mediaRttMs: Number.isFinite(pair?.currentRoundTripTime) ? Math.round(pair.currentRoundTripTime * 1000) : null,
    route: candidates.some(c => c.candidateType === 'relay') ? 'TURN relay' : candidates.length ? 'Direct' : null,
    videoKbps: bitrate(video, 'bytesSent', previous), audioKbps: bitrate(audio, 'bytesSent', previous),
    videoFps: Number.isFinite(video?.framesPerSecond) ? Math.round(video.framesPerSecond) : elapsed > 0 && frames !== null ? Math.round(frames * 1000 / elapsed) : null,
    width: video?.frameWidth || null, height: video?.frameHeight || null,
    sourceWidth: source?.width || null, sourceHeight: source?.height || null, sourceFps: Number.isFinite(source?.framesPerSecond) ? source.framesPerSecond : null,
    encodeMs: frames > 0 && encode !== null ? Math.round(encode * 1000 / frames) : null,
    qualityLimitation: video?.qualityLimitationReason || null,
    availableKbps: Number.isFinite(pair?.availableOutgoingBitrate) ? Math.round(pair.availableOutgoingBitrate / 1000) : null,
    videoCodec: stats.find(s => s.id === video?.codecId)?.mimeType || null,
  };
  for (const s of [video, audio]) if (s) previous.set(s.id, s);
  return result;
}
